import { Component, OnInit, TemplateRef, ViewChild, NgZone } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { ColDef } from 'ag-grid-community';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'app-partner-limits',
  templateUrl: './partner-limits.component.html',
  styleUrl: './partner-limits.component.scss'
})
export class PartnerLimitsComponent implements OnInit {
  partnerLimits: any[] = [];
  partners: any[] = [];
  loading = false;
  page = 1;
  limit = 500; // Get all records for AG Grid
  total = 0;
  gridApi: any;
  selectedLimit: any = null;
  addForm: any = {
    partnerId: '',
    dailyLimit: 0,
    monthlyLimit: 0,
    dailyTransactionLimit: 0,
    transactionLimits: {
      transfer: {
        minAmount: 0,
        maxAmount: 0
      }
    }
  };
  editForm: any = {
    dailyLimit: 0,
    monthlyLimit: 0,
    dailyTransactionLimit: 0,
    transactionLimits: {
      transfer: {
        minAmount: 0,
        maxAmount: 0
      }
    }
  };
  
  @ViewChild('editModal', { static: true }) editModal!: TemplateRef<any>;
  @ViewChild('addModal', { static: true }) addModal!: TemplateRef<any>;
  modalRef: any;
  addErrorMessage: string = '';
  editErrorMessage: string = '';
  
  @ViewChild('actionTemplate', { static: true }) actionTemplate!: TemplateRef<any>;

    columnDefs: ColDef[] = [
       {
      headerName: 'Action',
      field: 'action',
      cellRenderer: (params) => {
        const button = document.createElement('button');
        button.classList.add('btn', 'btn-sm', 'btn-primary', 'active', 'rounded-pill');
        button.innerHTML = '<i class="bi bi-eye-fill"></i>';
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.openEditModal(params.data);
          });
        });
        return button;
      },
      width: 100,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Partner Name',
      field: 'partnerId.name',
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => params.data.partnerId?.name || 'N/A'
    },
    {
      headerName: 'Partner Type',
      field: 'partnerId.type',
      sortable: true,
      filter: true,
      width: 150
    },

    {
      headerName: 'Daily Limit',
      field: 'dailyLimit',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => `${params.data.partnerCurrency}${params.value?.toLocaleString() || '0'}`
    },
    {
      headerName: 'Monthly Limit',
      field: 'monthlyLimit',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => `${params.data.partnerCurrency}${params.value?.toLocaleString() || '0'}`
    },
    {
      headerName: 'Transactions Per Day',
      field: 'dailyTransactionLimit',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => params.value?.toLocaleString() || '0'
    },
    {
      headerName: 'Transfer Min',
      field: 'transactionLimits.transfer.minAmount',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => `${params.data.partnerCurrency}${params.value?.toLocaleString() || '0'}`
    },
    {
      headerName: 'Transfer Max',
      field: 'transactionLimits.transfer.maxAmount',
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => `${params.data.partnerCurrency}${params.value?.toLocaleString() || '0'}`
    },
   
  ];

  gridOptions: any = {
    columnDefs: this.columnDefs,
    rowSelection: 'multiple',
    pagination: true,
    paginationPageSize: 20,
    domLayout: 'normal',
    defaultColDef: {
      editable: false,
      sortable: true,
      filter: true,
    },
    onGridReady: (params: any) => {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      window.addEventListener('resize', () => {
        setTimeout(() => params.api.sizeColumnsToFit());
      });
    }
  };

  constructor(
    private _apiService: ApiService,
    private modalService: NgbModal,
    private zone: NgZone
  ) { }

  ngOnInit() {
    this.loadPartnerLimits();
    this.getPartners();
  }

  getPartners() {
    this._apiService.getData('/admin/partners').subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.partners = res.data;
        }
      },
      error: () => {
        console.error('Error fetching partners');
      }
    });
  }

  openAddModal() {
    this.addForm = {
      partnerId: '',
      dailyLimit: 0,
      monthlyLimit: 0,
      dailyTransactionLimit: 0,
      transactionLimits: {
        transfer: {
          minAmount: 0,
          maxAmount: 0
        }
      }
    };
    this.modalRef = this.modalService.open(this.addModal, { backdrop: 'static', size: 'lg' });
  }

  isAddFormValid(): boolean {
    this.addErrorMessage = '';
    if (!this.addForm.partnerId) {
      this.addErrorMessage = 'Please select a partner';
      return false;
    }
    if (this.addForm.dailyLimit < 0) {
      this.addErrorMessage = 'Daily limit must be 0 or greater';
      return false;
    }
    if (this.addForm.monthlyLimit < 0) {
      this.addErrorMessage = 'Monthly limit must be 0 or greater';
      return false;
    }
    if (this.addForm.dailyTransactionLimit < 1) {
      this.addErrorMessage = 'Daily transaction limit must be at least 1';
      return false;
    }
    if (this.addForm.transactionLimits.transfer.minAmount < 0) {
      this.addErrorMessage = 'Transfer min amount must be 0 or greater';
      return false;
    }
    if (this.addForm.transactionLimits.transfer.maxAmount < 0) {
      this.addErrorMessage = 'Transfer max amount must be 0 or greater';
      return false;
    }
    if (this.addForm.transactionLimits.transfer.minAmount > this.addForm.transactionLimits.transfer.maxAmount) {
      this.addErrorMessage = 'Transfer minimum amount cannot exceed transfer maximum amount';
      return false;
    }
    return true;
  }

  addLimit() {
    if (!this.isAddFormValid()) {
      return;
    }

    const payload = {
      ...this.addForm,
    };

    this._apiService.postData('/admin/partner-limits', payload).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.loadPartnerLimits();
          this.closeModal();
        } else {
          this.addErrorMessage = res.message || 'Error adding partner limit';
        }
      },
      error: (err: any) => {
        this.addErrorMessage = (err.error && err.error.message) || err.message || 'Error adding partner limit';
      }
    });
  }

  loadPartnerLimits() {
    this.loading = true;
    // Get all records for AG Grid
    const query = `?page=0&limit=${this.limit}`;
    
    this._apiService.getData('/admin/partner-limits' + query).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.partnerLimits = res.data.partnerLimits || [];
          this.total = res.data.total || 0;
          if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this.partnerLimits);
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  deleteLimit(id: string) {
    if (confirm('Are you sure you want to delete this partner limit?')) {
      this._apiService.deleteData(`/admin/partner-limits/${id}`).subscribe({
        next: (res: any) => {
          if (res && !res.error) {
            this.loadPartnerLimits();
            alert('Partner limit deleted successfully!');
          } else {
            alert(res.message || 'Error deleting partner limit');
          }
        },
        error: (err: any) => {
          alert('Error deleting partner limit: ' + err.message);
        }
      });
    }
  }



  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
    window.addEventListener('resize', () => {
      setTimeout(() => params.api.sizeColumnsToFit());
    });
  }
  
  openEditModal(limit: any) {
    this.selectedLimit = { ...limit };
    this.editForm = {
      dailyLimit: limit.dailyLimit,
      monthlyLimit: limit.monthlyLimit,
      dailyTransactionLimit: limit.dailyTransactionLimit,
      transactionLimits: {
        transfer: { ...limit.transactionLimits?.transfer }
      }
    };
    this.modalRef = this.modalService.open(this.editModal, { backdrop: 'static', size: 'lg' });
  }
  
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }
  
  isFormValid(): boolean {
    this.editErrorMessage = '';
    if (this.editForm.dailyLimit < 0) {
      this.editErrorMessage = 'Daily limit must be 0 or greater';
      return false;
    }
    if (this.editForm.monthlyLimit < 0) {
      this.editErrorMessage = 'Monthly limit must be 0 or greater';
      return false;
    }
    if (this.editForm.dailyTransactionLimit < 1) {
      this.editErrorMessage = 'Daily transaction limit must be at least 1';
      return false;
    }
    if (this.editForm.transactionLimits.transfer.minAmount < 0) {
      this.editErrorMessage = 'Transfer min amount must be 0 or greater';
      return false;
    }
    if (this.editForm.transactionLimits.transfer.maxAmount < 0) {
      this.editErrorMessage = 'Transfer max amount must be 0 or greater';
      return false;
    }
    if (this.editForm.transactionLimits.transfer.minAmount > this.editForm.transactionLimits.transfer.maxAmount) {
      this.editErrorMessage = 'Transfer minimum amount cannot exceed transfer maximum amount';
      return false;
    }
    return true;
  }
  
  updateLimit() {
    if (!this.isFormValid()) {
      return;
    }
    
    if (!this.selectedLimit) {
      this.editErrorMessage = 'No limit selected for update';
      return;
    }
    
    const updateData = {
      dailyLimit: this.editForm.dailyLimit,
      monthlyLimit: this.editForm.monthlyLimit,
      dailyTransactionLimit: this.editForm.dailyTransactionLimit,
      transactionLimits: this.editForm.transactionLimits
    };
    
    this._apiService.putData(`/admin/partner-limits/${this.selectedLimit._id}`, updateData).subscribe({
      next: (res: any) => {
        if (res && !res.error) {
          this.loadPartnerLimits();
          this.closeModal();
        } else {
          this.editErrorMessage = res.message || 'Error updating partner limit';
        }
      },
      error: (err: any) => {
        this.editErrorMessage = (err.error && err.error.message) || err.message || 'Error updating partner limit';
      }
    });
  }
}
